import { useCallback, useRef } from 'react';
import { HubConnectionState } from '@microsoft/signalr';
import { useSignalRHub, type ConnectionStatus } from '@/realtime/useSignalRHub';
import type { NopFiltersDto } from '@/api/types/riskDashboard';
import type { NopLiveResultDto } from '@/api/types/consolidatedExposure';

interface NopLiveOptions {
  enabled: boolean;
  initialFilters: NopFiltersDto;
  onData: (data: NopLiveResultDto) => void;
  onError?: (message: string) => void;
}

// Consolidated Exposure live hub (/hubs/nop-live). Subscribes with the current
// NOP filters on (re)connect and exposes updateFilters() -> UpdateFilters.
// Mirrors nop-live-connection.js. (The Risk Dashboard DTO is composed from this
// same NopLiveResultDto server-side.)
export function useNopLive({
  enabled,
  initialFilters,
  onData,
  onError,
}: NopLiveOptions): { status: ConnectionStatus; updateFilters: (dto: NopFiltersDto) => void } {
  const filtersRef = useRef<NopFiltersDto>(initialFilters);

  const { status, connection } = useSignalRHub({
    url: '/hubs/nop-live',
    enabled,
    on: {
      OnNopDataReceived: (data) => onData(data as NopLiveResultDto),
      OnNopError: (message) => onError?.(String(message ?? 'Live NOP update failed.')),
    },
    onConnected: (conn) => {
      void conn.invoke('Subscribe', filtersRef.current).catch(() => {
        onError?.('Failed to connect to live NOP updates.');
      });
    },
  });

  const updateFilters = useCallback(
    (dto: NopFiltersDto) => {
      filtersRef.current = dto;
      const conn = connection.current;
      if (conn && conn.state === HubConnectionState.Connected) {
        void conn.invoke('UpdateFilters', dto).catch(() => onError?.('Failed to update live filters.'));
      }
    },
    [connection, onError],
  );

  return { status, updateFilters };
}
